//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
import { throttle, debounce } from "throttle-debounce";
// team-settings-search imports
import { IContainer, IProps, IState } from "./TeamSettingsSearch.d";
import { styles } from "./TeamSettingsSearch.styles";
import { teamSettingsSearchFragments, refetchQuery } from "./TeamSettingsSearch.gql";
// other imports
import { CalloutBox } from "@components/callout-box";
import { TeamSettingsTeammate } from "../team-settings-teammate";
// util
import {
  ITeammateStatusPerson,
  augmentOrgPerson,
  TeammateInviteStatusEnum,
  MAX_TEAM_SIZE,
} from "@util/team.util";
import { getCurrentWorkingGroup } from "@util/working-group.util";
import { track, TrackingEvents } from "@util/analytics.util";
import { logger } from "@util/logger.util";
// state
import { IReduxState } from "@reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { updateTeam, invitePersonsToTeam } from "@mutations";
// material-ui
import { Button } from "@clockwise/design-system";
import { InputAdornment, TextField, CircularProgress } from "@material-ui/core";
import { Clear, PersonAdd, Search, Warning } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
class TeamSettingsSearchCmpt extends React.Component<IProps, IState> {
  static defaultProps = {
    mode: "modify" as const,
  };

  private onUpdateInputDebounced: Function;
  private onUpdateInputThrottled: Function;
  private hasInitialized = false;

  private _lastQuery: string;
  private _augmentedPersonsSortOrder: Record<string, number>;

  constructor(props: IProps) {
    super(props);

    this.state = {
      isSearchMode: props.mode === "create",
      searchQuery: "",
      checkedMembers: {},
      loading: false,
    };

    // throttling and debouncing search
    this.onUpdateInputDebounced = debounce(500, this.onUpdateInputRefetch);
    this.onUpdateInputThrottled = throttle(500, this.onUpdateInputRefetch);
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { viewer, defaultMembers } = nextProps;
    const nextQueryMatchesSearch =
      viewer.orgPersonQueryResultErrorable &&
      viewer.orgPersonQueryResultErrorable.__typename !== "GraphEntityError" &&
      viewer.orgPersonQueryResultErrorable.query === this.state.searchQuery;

    if (this.state.loading && nextQueryMatchesSearch) {
      // cleanup any old unchecked cached members
      for (const calendarId in this.state.checkedMembers) {
        const checkedMember = this.state.checkedMembers[calendarId];

        if (!checkedMember.checked) {
          delete this.state.checkedMembers[calendarId];
        }
      }

      this.setState({
        loading: false,
        checkedMembers: this.state.checkedMembers,
      });
    }

    if (
      this.props.defaultMembers.some(
        (dm) => defaultMembers.findIndex((m) => m.person.personId === dm.person.personId) === -1,
      )
    ) {
      this.setState(
        {
          isSearchMode: nextProps.mode === "create",
          searchQuery: "",
        },
        () => {
          this.onUpdateInputRefetch("", []);
        },
      );
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private getAugmentedPersonsFromSearchResults = (
    orgPersonQueryResult: ISchema.IOrgPersonQueryResult,
  ) => {
    const { team, org } = this.props;
    const workingGroup = getCurrentWorkingGroup(org);
    const suggestedCalendarIds = (workingGroup && workingGroup.calendarIds) || [];

    let orgPersons: ISchema.IOrgPerson[] = orgPersonQueryResult.persons;

    if (!orgPersonQueryResult.query) {
      orgPersons = orgPersons
        .filter((person) => !this.state.checkedMembers[person.primaryCalendarId])
        .concat(Object.values(this.state.checkedMembers).map(({ person }) => person));
    }

    const persons = orgPersons.map((person: ISchema.IOrgPerson) => {
      const { teamMembers, invitedMembers } = team || {
        teamMembers: [] as ISchema.ITeamMember[],
        invitedMembers: [] as ISchema.ITeamMember[],
      };

      const checkedMember = person && this.state.checkedMembers[person.primaryCalendarId];
      const invitedMember = person && this.props.invitedMembers[person.primaryCalendarId];

      // check if person is already team member
      const maybeTeamMember = teamMembers.find(
        (tm) => tm.person.primaryCalendarId === person.primaryCalendarId,
      );
      if (maybeTeamMember) {
        return augmentOrgPerson(
          person,
          TeammateInviteStatusEnum.Accepted,
          maybeTeamMember.role,
          !!(checkedMember && checkedMember.checked),
          suggestedCalendarIds.findIndex((c) => c === person.primaryCalendarId) > -1,
          !!(invitedMember && invitedMember.invited),
        );
      }

      // check if person is already invited
      const maybeInvitedMember = invitedMembers.find(
        (im) => im.person.primaryCalendarId === person.primaryCalendarId,
      );
      if (maybeInvitedMember) {
        return augmentOrgPerson(
          person,
          TeammateInviteStatusEnum.Pending,
          maybeInvitedMember.role,
          !!(checkedMember && checkedMember.checked),
          suggestedCalendarIds.findIndex((c) => c === person.primaryCalendarId) > -1,
          !!(invitedMember && invitedMember.invited),
        );
      }

      return augmentOrgPerson(
        person,
        TeammateInviteStatusEnum.Uninvited,
        undefined,
        !!(checkedMember && checkedMember.checked),
        suggestedCalendarIds.findIndex((c) => c === person.primaryCalendarId) > -1,
        !!(invitedMember && invitedMember.invited),
      );
    });

    if (this._augmentedPersonsSortOrder && orgPersonQueryResult.query === this._lastQuery) {
      return persons.sort(
        (a, b) =>
          this._augmentedPersonsSortOrder[a.person.primaryCalendarId] -
          this._augmentedPersonsSortOrder[b.person.primaryCalendarId],
      );
    }

    const sortedAugmentedPersons = persons.sort((a, b) => {
      if (
        this.state.checkedMembers[a.person.primaryCalendarId] &&
        !this.state.checkedMembers[b.person.primaryCalendarId]
      ) {
        return 1;
      } else if (
        this.state.checkedMembers[b.person.primaryCalendarId] &&
        !this.state.checkedMembers[a.person.primaryCalendarId]
      ) {
        return -1;
      } else if (a.isSuggested && !b.isSuggested) {
        return -1;
      } else if (!a.isSuggested && b.isSuggested) {
        return 1;
      } else if (
        a.inviteStatus === TeammateInviteStatusEnum.Uninvited &&
        a.inviteStatus !== b.inviteStatus
      ) {
        return -1;
      } else if (
        b.inviteStatus === TeammateInviteStatusEnum.Uninvited &&
        a.inviteStatus !== b.inviteStatus
      ) {
        return 1;
      } else if (
        a.inviteStatus === TeammateInviteStatusEnum.Pending &&
        a.inviteStatus !== b.inviteStatus
      ) {
        return -1;
      } else if (
        b.inviteStatus === TeammateInviteStatusEnum.Pending &&
        a.inviteStatus !== b.inviteStatus
      ) {
        return 1;
      }

      const aGivenName = (a.person.profile && a.person.profile.givenName) || "";
      const bGivenName = (b.person.profile && b.person.profile.givenName) || "";

      const aFamilyName = (a.person.profile && a.person.profile.familyName) || "";
      const bFamilyName = (b.person.profile && b.person.profile.familyName) || "";

      return aGivenName.localeCompare(bGivenName) || aFamilyName.localeCompare(bFamilyName);
    });

    this._augmentedPersonsSortOrder = {};
    this._lastQuery = orgPersonQueryResult.query;

    sortedAugmentedPersons.forEach((person, i) => {
      this._augmentedPersonsSortOrder[person.person.primaryCalendarId] = i;
    });

    return sortedAugmentedPersons;
  };

  private onUpdateInputRefetch(queryInput: string, excludeCalendars: string[]) {
    const { org } = this.props;
    this.setState({ loading: true }, () =>
      this.props.relay.refetch({ orgRelayId: org.id, queryInput, excludeCalendars }),
    );
  }

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  private onClickSearchButton = (isSearchMode: boolean) => {
    this.setState({ isSearchMode });
    if (this.state.searchQuery !== "") {
      this.onUpdateInput("");
    }
    track(TrackingEvents.TEAMS.TEAM_SETTING_SEARCH_CLICK_SEARCH_TOGGLE, {
      showSearch: isSearchMode,
    });
  };

  private onUpdateInput = (searchQuery: string) => {
    const { org } = this.props;

    if (searchQuery.length < 5) {
      this.onUpdateInputThrottled(searchQuery, [org.primaryOrgCalendar]);
    } else {
      this.onUpdateInputDebounced(searchQuery, [org.primaryOrgCalendar]);
    }

    this.setState({ searchQuery });
  };

  private onInvite = (person: ISchema.IOrgPerson, resend?: boolean) => {
    const { relay, org, team } = this.props;

    if (!team) {
      return;
    }

    if (resend) {
      invitePersonsToTeam(
        relay.environment,
        { orgRelayId: org.id, teamId: team.teamId, personIds: [person.personId] },
        () => {
          this.props.snackBarOpen("Invite resent!");
        },
        () => {
          this.props.snackBarOpen("Failed to resend invite");
          logger.error("Failed to resend invite");
        },
      );

      track(TrackingEvents.TEAMS.TEAM_SETTING_SEARCH_CLICK_RESEND_INVITE, {
        calendarId: person.primaryCalendarId,
      });
    } else {
      updateTeam(
        relay.environment,
        {
          orgRelayId: org.id,
          teamId: team.teamId,
          addedTeamMembers: [{ personId: person.personId, role: ISchema.TeamMemberRole.Member }],
        },
        () => {
          const msg = person.userId ? "This user was added to your team!" : "Invite sent!";
          this.props.snackBarOpen(msg);
        },
        () => {
          const msg = person.userId ? "Failed to add user to your team!" : "Failed to send invite!";
          this.props.snackBarOpen(msg);
          logger.error(msg);
        },
      );

      invitePersonsToTeam(
        relay.environment,
        { orgRelayId: org.id, teamId: team.teamId, personIds: [person.personId] },
        () => null,
        () => {
          logger.error("Failed to send invite");
        },
      );

      track(TrackingEvents.TEAMS.TEAM_SETTING_SEARCH_CLICK_INVITE, {
        calendarId: person.primaryCalendarId,
      });
    }

    if (this.props.onInvitedMembersChange) {
      const invitedMembers = this.props.invitedMembers;
      invitedMembers[person.primaryCalendarId] = { person, invited: true };
      this.props.onInvitedMembersChange(invitedMembers);
    }
  };

  private onDelete = (person: ISchema.IOrgPerson) => {
    const { relay, org, team } = this.props;

    if (!team) {
      return;
    }

    updateTeam(
      relay.environment,
      {
        orgRelayId: org.id,
        teamId: team.teamId,
        removedTeamMembers: [person.personId],
      },
      () => {
        this.props.snackBarOpen("Removed!");
        if (org.primaryOrgCalendar === person.primaryCalendarId && this.props.refetchTeams) {
          this.props.refetchTeams(); // refetch teams if removing yourself
        }
      },
      () => {
        this.props.snackBarOpen("Failed to remove teammate");
        logger.error("Failed to remove teammate");
      },
    );

    track(TrackingEvents.TEAMS.TEAM_SETTING_SEARCH_CLICK_DELETE, { isSelf: person.isYou });

    if (this.props.onInvitedMembersChange) {
      const invitedMembers = this.props.invitedMembers;
      invitedMembers[person.primaryCalendarId] = { person, invited: false };
      this.props.onInvitedMembersChange(invitedMembers);
    }
  };

  private onCheck = (person: ISchema.IOrgPerson, checked: boolean) => {
    this.state.checkedMembers[person.primaryCalendarId] = { person, checked };
    this.setState({ checkedMembers: this.state.checkedMembers });

    if (this.props.onCheck) {
      this.props.onCheck(person, checked);
    }

    track(TrackingEvents.TEAMS.TEAM_SETTING_SEARCH_CLICK_CHECK, { checked });
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderSearch = () => {
    const { classes, mode, team } = this.props;
    const { isSearchMode, searchQuery, loading } = this.state;

    const { teamMembers, invitedMembers } = team || {
      teamMembers: [] as ISchema.ITeamMember[],
      invitedMembers: [] as ISchema.ITeamMember[],
    };
    const isTeamMaxSize = teamMembers.length + invitedMembers.length >= MAX_TEAM_SIZE;
    if (isTeamMaxSize) {
      return (
        <CalloutBox warning style={styles.teamSizeWarning}>
          <Warning style={styles.warningIcon} />
          <span style={styles.warningLabel}>
            This team has reached its maximum size of 50 people
          </span>
        </CalloutBox>
      );
    }

    if (isSearchMode) {
      const variant: any = mode === "create" ? "standard" : "outlined";

      return (
        <div className="cw-p-3 cw-flex cw-items-center cw-gap-2 cw-text-slate-standard">
          <TextField
            placeholder="Search for new teammates to invite"
            variant={variant}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search classes={{ root: "cw-w-5 cw-text-slate-standard" }} />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={24} />
                </InputAdornment>
              ) : null,
              classes: { input: classes.searchInput },
            }}
            value={searchQuery}
            onChange={(e) => this.onUpdateInput(e.target.value)}
            autoFocus={mode !== "create"}
            fullWidth
          />
          {/* if mode is not create, clear field and go back to view only mode */}
          <Button
            size="large"
            variant="text"
            startIcon={Clear}
            onClick={() => this.onClickSearchButton(mode === "create")}
          />
        </div>
      );
    }

    return (
      <div className="cw-p-3">
        <Button
          size="large"
          variant="text"
          sentiment="positive"
          startIcon={PersonAdd}
          onClick={() => this.onClickSearchButton(true)}
        >
          Invite new teammate
        </Button>
      </div>
    );
  };

  private renderResults = () => {
    const { viewer, defaultMembers, classes, mode } = this.props;
    const { isSearchMode, searchQuery } = this.state;

    if (!isSearchMode && mode !== "create") {
      const sortedMembers = [...defaultMembers].sort((a, b) => {
        if (a.person.isYou && !b.person.isYou) {
          return -1;
        } else if (b.person.isYou && !a.person.isYou) {
          return 1;
        }

        const aGivenNameString = a.person.profile
          ? a.person.profile.givenName
          : a.person.primaryCalendarId;
        const bGivenNameString = b.person.profile
          ? b.person.profile.givenName
          : b.person.primaryCalendarId;

        const aFamilyNameString = a.person.profile
          ? a.person.profile.familyName
          : a.person.primaryCalendarId;
        const bFamilyNameString = b.person.profile
          ? b.person.profile.familyName
          : b.person.primaryCalendarId;

        return (
          aGivenNameString.localeCompare(bGivenNameString) ||
          aFamilyNameString.localeCompare(bFamilyNameString)
        );
      });

      return <div className={classes.results}>{sortedMembers.map(this.renderRow)}</div>;
    }

    if (
      // !viewer.orgPersonQueryResultErrorable ||
      viewer.orgPersonQueryResultErrorable.__typename === "GraphEntityError"
    ) {
      logger.error("failed to search");
      return null;
    }

    const persons = this.getAugmentedPersonsFromSearchResults(viewer.orgPersonQueryResultErrorable);

    // weird to do here but its ensured to only happen once
    if (!this.hasInitialized && this.props.onInit) {
      this.hasInitialized = true;

      this.props.onInit({
        nDisplayedInviteSuggestions: persons.filter((p) => p.isSuggested).length,
      });
    }

    if (!persons.length && mode === "create" && searchQuery.length) {
      return (
        <div className={classes.results}>
          <div className="no-results">No results found</div>
        </div>
      );
    }

    return <div className={classes.results}>{persons.map(this.renderRow)}</div>;
  };

  private renderRow = (teammate: ITeammateStatusPerson, i: number) => {
    const { org, mode } = this.props;
    const { isSearchMode } = this.state;

    return (
      <TeamSettingsTeammate
        key={i}
        org={org}
        mode={mode}
        teammate={teammate}
        onInvite={this.onInvite}
        onDelete={this.onDelete}
        onCheck={this.onCheck}
        isSearchResult={mode === "create" || isSearchMode}
      />
    );
  };

  public render() {
    const { classes, containerClassName, render } = this.props;

    if (render) {
      return render({
        classes,
        renderSearch: this.renderSearch,
        renderResults: this.renderResults,
      });
    }

    return (
      <div className={`${classes.container}${containerClassName ? ` ${containerClassName}` : ""}`}>
        {this.renderSearch()}
        {this.renderResults()}
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
export const TeamSettingsSearchStyled = withStyles(styles)(TeamSettingsSearchCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const TeamSettingsSearchRedux = connect(mapStateToProps, {
  snackBarOpen,
})(TeamSettingsSearchStyled);

//////////////////
// RELAY
//////////////////
export const TeamSettingsSearch = createRefetchContainer<IContainer>(
  TeamSettingsSearchRedux,
  teamSettingsSearchFragments,
  refetchQuery,
);
